import React from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Calendar, ArrowLeft } from 'lucide-react';
import Layout from '../components/Layout';
import useTheme from '../hooks/useTheme';
import { getBlogPost } from '../data/blogPosts';

const BlogPost = () => {
  const { slug } = useParams();
  const { darkMode, toggleDarkMode, cardClass } = useTheme();
  const post = getBlogPost(slug, 'pl');

  if (!post) {
    return <Navigate to="/blog" replace />;
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pl-PL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>{post.title} | Galony&Litry Blog</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={`https://galonyilitry.pl/blog/${post.slug}`} />
      </Helmet>

      <Layout darkMode={darkMode} toggleDarkMode={toggleDarkMode} lang="pl">
        <div className="max-w-3xl mx-auto">
          {/* Back Link */}
          <Link
            to="/blog"
            className={`inline-flex items-center mb-6 font-medium ${darkMode ? 'text-cyan-400 hover:text-cyan-300' : 'text-blue-600 hover:text-blue-700'} transition-colors`}
          >
            <ArrowLeft size={16} className="mr-1" />
            Wróć do bloga
          </Link>

          {/* Article */}
          <article className={`${cardClass} rounded-2xl border overflow-hidden`}>
            <div className="p-8">
              {/* Header */}
              <header className="mb-8">
                <div className={`flex items-center text-sm mb-4 ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  <Calendar size={14} className="mr-2" />
                  {formatDate(post.date)}
                </div>

                <h1 className={`text-2xl md:text-3xl font-bold ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  {post.title}
                </h1>
              </header>

              {/* Content */}
              <div
                className={`prose max-w-none ${darkMode ? 'prose-invert' : ''}`}
                style={{
                  '--tw-prose-headings': darkMode ? '#fff' : '#1e293b',
                  '--tw-prose-body': darkMode ? '#cbd5e1' : '#475569',
                  '--tw-prose-bold': darkMode ? '#fff' : '#1e293b',
                  '--tw-prose-bullets': darkMode ? '#94a3b8' : '#64748b',
                }}
              >
                <style>{`
                  .blog-content h2 {
                    font-weight: 600;
                    margin-bottom: 1rem;
                    margin-top: 1.5rem;
                    font-size: 1.25rem;
                    color: ${darkMode ? '#fff' : '#1e293b'};
                  }
                  .blog-content h2:first-child {
                    margin-top: 0;
                  }
                `}</style>
                <div
                  className={`blog-content ${darkMode ? 'text-slate-300' : 'text-slate-600'} leading-relaxed text-left`}
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </div>
            </div>
          </article>

          {/* Related Links */}
          <div className={`mt-8 p-6 ${cardClass} rounded-2xl border`}>
            <h3 className={`font-bold mb-4 ${darkMode ? 'text-white' : 'text-slate-800'}`}>
              Przydatne narzędzia
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Link
                to="/przelicznik-galony-na-litry"
                className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-blue-50 hover:bg-blue-100'} transition-colors`}
              >
                <div className={`font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  Przelicznik galony → litry
                </div>
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Szybka konwersja jednostek
                </div>
              </Link>
              <Link
                to="/przelicznik-litry-na-galony"
                className={`p-4 rounded-xl ${darkMode ? 'bg-slate-700 hover:bg-slate-600' : 'bg-blue-50 hover:bg-blue-100'} transition-colors`}
              >
                <div className={`font-medium ${darkMode ? 'text-white' : 'text-slate-800'}`}>
                  Przelicznik litry → galony
                </div>
                <div className={`text-sm ${darkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                  Szybka konwersja jednostek
                </div>
              </Link>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default BlogPost;
